// To see list of smart car makes: https://smartcar.com/docs/api/?version=v1.0&language=Javascript#makes

import { remoteConfig } from '@/firebase'
import { useAppSelector } from '@/hooks'
import useMultiProgramFlowControl from '@/hooks/useMultiProgramFlowControl'
import {
  programsNewCollection,
  selectAvailablePrograms,
  selectProgram,
} from '@/reducers/programsNew'
import { match, P } from 'ts-pattern'
import { VehicleBrand } from './brand'
import { eligibleMakesCollection } from '@/reducers/eligibleMakes'
import { VehicleMakeModels } from '@/types/utilityProgram'

type ProgramMakeLists = {
  excluded: VehicleMakeModels[]
  eligible: VehicleMakeModels[]
}

type ProgramWithMakeLists = {
  excluded_vehicle_make_models?: VehicleMakeModels[]
  eligible_vehicle_make_models?: VehicleMakeModels[]
}

function toMakeLists(
  program: ProgramWithMakeLists | null | undefined,
): ProgramMakeLists {
  return {
    excluded: program?.excluded_vehicle_make_models ?? [],
    eligible: program?.eligible_vehicle_make_models ?? [],
  }
}

// The backend stores makes as lowercase snake ("mercedes_benz", "land_rover") while the brand
// list below keys off kebab ids ("mercedes-benz", "land-rover"). Strip everything that isn't
// alphanumeric so the two always line up, matching how the backend compares makes.
function normalizeMake(make: string) {
  return make.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()
}

/**
 * Whether a brand should be tagged ineligible in the connect-device picker.
 *
 * True when the program explicitly excludes the make, and also when the program runs a positive
 * allow list that doesn't name it. Model-level eligibility deliberately isn't decided here: the
 * connect flow has no model picker, so a make that IS on the allow list goes through and the
 * model is only checked once the device has been onboarded.
 */
export function isMakeIneligibleForProgram(
  makeId: string | undefined,
  { excluded, eligible }: ProgramMakeLists,
) {
  if (!makeId) {
    return false
  }
  const normalized = normalizeMake(makeId)

  if (
    excluded.some((makeModel) => normalizeMake(makeModel.make) === normalized)
  ) {
    return true
  }
  // An empty allow list means the program doesn't restrict by make at all.
  if (eligible.length === 0) {
    return false
  }
  return !eligible.some(
    (makeModel) => normalizeMake(makeModel.make) === normalized,
  )
}

/**
 * Whether a brand is ineligible across every program in scope.
 *
 * Outside an enrolment flow there is no single program to answer for, so the picker falls back
 * to all of the user's available programs and only tags a make when *none* of them accept it --
 * tagging a make that some other available program would happily take would be a lie.
 */
export function isMakeIneligibleForPrograms(
  makeId: string | undefined,
  programs: ProgramMakeLists[],
) {
  if (!makeId || programs.length === 0) {
    return false
  }
  return programs.every((program) =>
    isMakeIneligibleForProgram(makeId, program),
  )
}

/**
 * Whether a brand should be tagged ineligible in the connect-device picker.
 *
 * A brand we can't connect at all (the static `showApology` flag on `makes`, e.g. the ChargeScape
 * brands and Honda / Acura) is ineligible regardless of program: tapping it only ever reaches the
 * apology page, so showing it untagged among the connectable brands implies a connection we don't
 * offer. Programs only list the makes they explicitly rule out, and most don't list these.
 *
 * Deliberately reads the brand list's own flag rather than the state-derived `showApology` from
 * STATE_ELIGIBLE_MAKES, which would tag every non-Tesla make in a state configured for Tesla only.
 */
export function isBrandIneligible(
  brand: Pick<VehicleBrand, 'id' | 'showApology'>,
  programs: ProgramMakeLists[],
) {
  return (
    Boolean(brand.showApology) ||
    isMakeIneligibleForPrograms(brand.id, programs)
  )
}

/**
 * Sinks ineligible brands to the bottom of the picker.
 *
 * A program with a positive allow list rules out most of the ~44 brands, which left a long run
 * of unusable cards between the user and the one they wanted. This is a stable partition, not a
 * sort: order within each group is the curated order of `makes` below — Tesla first as the most
 * common, then alphabetical — so pinning stays a property of that list rather than something
 * restated here. It also means a program that somehow ruled Tesla out would still show it first
 * among the ineligible, rather than buried alphabetically.
 *
 * A no-op when nothing is ineligible.
 */
export function sortEligibleMakesFirst<T extends { ineligible: boolean }>(
  brands: T[],
): T[] {
  return [
    ...brands.filter((brand) => !brand.ineligible),
    ...brands.filter((brand) => brand.ineligible),
  ]
}

export function useVehicleMakes() {
  const { isLoadingOrIdle } = programsNewCollection.useFetch()
  const fromPartnerOnly = remoteConfig?.getBoolean(
    'back_soon_from_partner_only',
  )
  const backSoonBrands = remoteConfig?.getArray('back_soon_brands') ?? []
  const { data: eligibleMakes, isLoadingOrIdle: isLoadingEligibleMakes } =
    eligibleMakesCollection.useFetch()
  const stateConfiguredEligibleMakes = eligibleMakes.map((make) =>
    make.make.toLowerCase(),
  )
  const isFromPartner = useAppSelector((state) => state.auth.fromToken)
  const availablePrograms = useAppSelector(selectAvailablePrograms())
  const { multiProgramFlowProgramId } = useMultiProgramFlowControl()
  const flowProgram = useAppSelector(
    selectProgram(multiProgramFlowProgramId ?? -1),
  )

  const programMakeLists = match({
    isFromPartner,
    availablePrograms,
    flowProgram,
  })
    .with({ isFromPartner: true }, ({ availablePrograms }) =>
      availablePrograms.slice(0, 1).map(toMakeLists),
    )
    .with({ flowProgram: P.nonNullable }, ({ flowProgram }) => [
      toMakeLists(flowProgram.utility_program),
    ])
    // No explicit program in scope -- the picker was opened outside an enrolment flow. Answer
    // for every program available to the user rather than tagging nothing at all.
    .otherwise(({ availablePrograms }) => availablePrograms.map(toMakeLists))

  const processedMakes: ProcessedVehicleMake[] = makes.map((make) => {
    const isInBackSoonMakes =
      make.smartcarMake !== undefined &&
      backSoonBrands
        .map((brand) => brand.toLowerCase())
        .includes(make.smartcarMake?.toLowerCase())
    return {
      ...make,
      backSoon: Boolean(
        fromPartnerOnly
          ? isInBackSoonMakes && isFromPartner
          : isInBackSoonMakes,
      ),
      showApology:
        make.showApology ||
        !stateConfiguredEligibleMakes?.includes(make.id ?? ''),
      hidden: false,
      ineligible: isBrandIneligible(make, programMakeLists),
    }
  })

  return {
    vehicleMakes: sortEligibleMakesFirst(processedMakes),
    isLoading: isLoadingOrIdle || isLoadingEligibleMakes,
  }
}

export type ProcessedVehicleMake = VehicleBrand & {
  backSoon: boolean
  hidden: boolean
  ineligible: boolean
}

export const makes: VehicleBrand[] = [
  {
    id: 'tesla',
    name: 'Tesla',
    logo: '/dashboard/img/brands/tesla.webp',
    smartcarMake: 'TESLA',
  },
  {
    id: 'acura',
    name: 'Acura',
    logo: '/dashboard/img/brands/acura.svg',
    smartcarMake: 'ACURA',
    showApology: true,
  },
  {
    id: 'alfa-romeo',
    name: 'Alfa Romeo',
    logo: '/dashboard/img/brands/alfa_romeo.webp',
    smartcarMake: 'ALFA_ROMEO',
    showApology: false,
  },
  {
    id: 'audi',
    name: 'Audi',
    logo: '/dashboard/img/brands/audi.svg',
    smartcarMake: 'AUDI',
  },
  {
    id: 'bentley',
    name: 'Bentley',
    logo: '/dashboard/img/brands/bentley.svg',
    showApology: true,
  },
  {
    id: 'bmw',
    name: 'BMW',
    logo: '/dashboard/img/brands/bmw.svg',
    smartcarMake: 'BMW',
    showApology: true,
  },
  {
    id: 'cadillac',
    name: 'Cadillac',
    logo: '/dashboard/img/brands/cadillac.svg',
    smartcarMake: 'CADILLAC',
  },
  {
    id: 'chevrolet',
    name: 'Chevrolet',
    logo: '/dashboard/img/brands/chevrolet.svg',
    smartcarMake: 'CHEVROLET',
  },
  {
    id: 'chrysler',
    name: 'Chrysler',
    logo: '/dashboard/img/brands/chrysler.svg',
    smartcarMake: 'CHRYSLER',
  },
  {
    id: 'dodge',
    name: 'Dodge',
    logo: '/dashboard/img/brands/dodge.svg',
    smartcarMake: 'DODGE',
    showApology: false,
  },
  {
    id: 'ferrari',
    name: 'Ferrari',
    logo: '/dashboard/img/brands/ferrari.svg',
    showApology: true,
  },
  {
    id: 'fiat',
    name: 'Fiat',
    logo: '/dashboard/img/brands/fiat.svg',
    smartcarMake: 'FIAT',
    showApology: false,
  },
  {
    id: 'ford',
    name: 'Ford',
    logo: '/dashboard/img/brands/ford.svg',
    smartcarMake: 'FORD',
    showApology: true,
  },
  {
    id: 'genesis',
    name: 'Genesis',
    logo: '/dashboard/img/brands/genesis.svg',
    showApology: true,
  },
  {
    id: 'gmc',
    name: 'GMC',
    logo: '/dashboard/img/brands/gmc.svg',
    smartcarMake: 'GMC',
  },
  {
    id: 'honda',
    name: 'Honda',
    logo: '/dashboard/img/brands/honda.svg',
    smartcarMake: 'HONDA',
    showApology: true,
  },
  {
    id: 'hyundai',
    name: 'Hyundai',
    logo: '/dashboard/img/brands/hyundai.svg',
    smartcarMake: 'HYUNDAI',
  },
  {
    id: 'jaguar',
    name: 'Jaguar',
    logo: '/dashboard/img/brands/jaguar.svg',
    smartcarMake: 'JAGUAR',
  },
  {
    id: 'jeep',
    name: 'Jeep',
    logo: '/dashboard/img/brands/jeep.svg',
    smartcarMake: 'JEEP',
  },
  {
    id: 'kandi',
    name: 'Kandi',
    logo: '/dashboard/img/brands/kandi.jpg',
    showApology: true,
  },
  {
    id: 'kia',
    name: 'Kia',
    logo: '/dashboard/img/brands/kia.svg',
    smartcarMake: 'KIA',
  },
  {
    id: 'land-rover',
    name: 'Land Rover',
    logo: '/dashboard/img/brands/land_rover.svg',
    smartcarMake: 'LAND_ROVER',
  },
  {
    id: 'lexus',
    name: 'Lexus',
    logo: '/dashboard/img/brands/lexus.svg',
    smartcarMake: 'LEXUS',
  },
  {
    id: 'lincoln',
    name: 'Lincoln',
    logo: '/dashboard/img/brands/lincoln.svg',
    smartcarMake: 'LINCOLN',
  },
  {
    id: 'lucid',
    name: 'Lucid',
    logo: '/dashboard/img/brands/lucid.svg',
    showApology: true,
  },
  {
    id: 'maserati',
    name: 'Maserati',
    logo: '/dashboard/img/brands/maserati.svg',
    showApology: true,
  },
  {
    id: 'mazda',
    name: 'Mazda',
    logo: '/dashboard/img/brands/mazda.svg',
    smartcarMake: 'MAZDA',
    showApology: false,
  },
  {
    id: 'mclaren-automotive',
    name: 'McLaren Automotive',
    logo: '/dashboard/img/brands/mclaren_automotive.svg',
    showApology: true,
  },
  {
    id: 'mercedes-benz',
    name: 'Mercedes-Benz',
    logo: '/dashboard/img/brands/mercedes_benz.svg',
    smartcarMake: 'MERCEDES-BENZ',
    showApology: false,
  },
  {
    id: 'mini',
    name: 'Mini',
    logo: '/dashboard/img/brands/mini.svg',
    smartcarMake: 'MINI',
    showApology: true,
  },
  {
    id: 'mitsubishi',
    name: 'Mitsubishi',
    logo: '/dashboard/img/brands/mitsubishi.svg',
    showApology: true,
  },
  {
    id: 'nissan',
    name: 'Nissan',
    logo: '/dashboard/img/brands/nissan.svg',
    smartcarMake: 'NISSAN',
    showApology: true,
  },
  {
    id: 'polestar',
    name: 'Polestar',
    logo: '/dashboard/img/brands/polestar.svg',
    showApology: true,
  },
  {
    id: 'porsche',
    name: 'Porsche',
    logo: '/dashboard/img/brands/porsche.svg',
    smartcarMake: 'PORSCHE',
  },
  {
    id: 'ram',
    name: 'Ram',
    logo: '/dashboard/img/brands/ram.png',
    smartcarMake: 'RAM',
  },
  {
    id: 'rivian',
    name: 'Rivian',
    logo: '/dashboard/img/brands/rivian.svg',
    smartcarMake: 'RIVIAN',
  },
  {
    id: 'rolls-royce',
    name: 'Rolls-Royce',
    logo: '/dashboard/img/brands/rolls_royce.svg',
    showApology: true,
  },
  {
    id: 'smart',
    name: 'Smart',
    logo: '/dashboard/img/brands/smart.svg',
    showApology: true,
  },
  {
    id: 'subaru',
    name: 'Subaru',
    logo: '/dashboard/img/brands/subaru.webp',
    smartcarMake: 'SUBARU',
    showApology: false,
  },
  {
    id: 'toyota',
    name: 'Toyota',
    logo: '/dashboard/img/brands/toyota.svg',
    smartcarMake: 'TOYOTA',
  },
  {
    id: 'vinfast',
    name: 'Vinfast',
    logo: '/dashboard/img/brands/vinfast.svg',
    showApology: true,
  },
  {
    id: 'volkswagen',
    name: 'Volkswagen',
    logo: '/dashboard/img/brands/volkswagen.svg',
    smartcarMake: 'VOLKSWAGEN',
  },
  {
    id: 'volvo',
    name: 'Volvo',
    logo: '/dashboard/img/brands/volvo.svg',
    smartcarMake: 'VOLVO',
  },
]
